import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, ENUMS, SALES_CHANNELS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const whNameAutocompleteColumns = [{ accessor: 'Name', width: 200 }];

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const defaultColSpan = {
	md: 6
};

const PickupLocations = ({ locations }) => {
	const { control, watch, setValue, clearErrors } = useFormContext();
	const saleChannelLocationArray = useMemo(() => locations?.map((item) => item.id), [locations]);
	const { fields, append, remove } = useFieldArray({ control, name: 'pickupLocationList' });

	const columns = useMemo(
		() => [
			{
				header: 'warehouse',
				id: 'warehouse',
				required: true,
				renderCell: ({ row: { index } }) => (
					<AutoComplete
						autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
						value={watch(`pickupLocationList[${index}].warehouseID`)}
						columns={whNameAutocompleteColumns}
						selectionLabelAccessor={['Name']}
						name={`pickupLocationList[${index}].warehouseID`}
						setValue={(value) => {
							setValue(`pickupLocationList[${index}].warehouseID`, value?.ID ?? null, validateOptions);
							if (value?.ID) {
								clearErrors(`pickupLocationList[${index}].branchID`);
								setValue(`pickupLocationList[${index}].branchID`, null, validateOptions);
							}
						}}
					/>
				)
			},
			{
				header: 'branch',
				id: 'branch',
				required: true,
				renderCell: ({ row: { index } }) => (
					<AutoComplete
						autoCompleteName={AUTOCOMPLETE.BRANCHES}
						value={watch(`pickupLocationList[${index}].branchID`)}
						columns={whNameAutocompleteColumns}
						selectionLabelAccessor={['Name']}
						name={`pickupLocationList[${index}].branchID`}
						setValue={(value) => {
							setValue(`pickupLocationList[${index}].branchID`, value?.ID ?? null, validateOptions);
							if (value?.ID) {
								clearErrors(`pickupLocationList[${index}].warehouseID`);
								setValue(`pickupLocationList[${index}].warehouseID`, null, validateOptions);
							}
						}}
					/>
				)
			},
			{
				header: 'salesChannelLocationId',
				id: 'salesChannelLocationId',
				required: true,
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						searchable={false}
						name={`pickupLocationList[${index}].salesChannelLocationID`}
						data={saleChannelLocationArray}
						setValue={(value) => {
							setValue(
								`pickupLocationList[${index}].salesChannelLocationID`,
								value ?? null,
								validateOptions
							);
						}}
						value={watch(`pickupLocationList[${index}].salesChannelLocationID`)}
						renderOption={(option) => (
							<Typography>{locations?.find((item) => item.id === option)?.locationName}</Typography>
						)}
						renderSelectionValue={(value) => (
							<Typography fontWeight="500" color={'var(--black)'}>
								{locations?.find((item) => item.id === value)?.locationName}
							</Typography>
						)}
					/>
				)
			},
			{
				id: 'isLocationActive',
				header: 'isLocationActive',
				size: 60,
				renderCell: ({ row: { index } }) => (
					<SwitchInput
						className="mx-auto"
						name={`pickupLocationList[${index}].isLocationActive`}
						style={{ width: 'auto' }}
					/>
				)
			},
			{
				id: 'deleteRow',
				size: 30,
				maxWidth: 30
			}
		],
		[locations]
	);

	return (
		<Table
			data={fields}
			columns={columns}
			onRowDelete={(row) => remove(row.index)}
			onRowAdd={() => append({})}
			showAddNewRowButton
		/>
	);
};

const InventorySyncSettingsForm = NiceModal.create(({ credentialsID, storeName }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					inventoryCalculateMethod: yup.string().nullable().required(),
					inventoryUpdateMethod: yup.string().nullable().required(),
					inventorySync: yup.boolean().nullable(),
					multiLocation: yup.boolean().nullable(),

					pickupLocationList: yup.array().of(
						yup.object().shape({
							warehouseID: yup
								.number()
								.nullable()
								.when('branchID', ([branchID], schema) => (branchID ? schema : schema.required())),
							branchID: yup.number().nullable(),
							salesChannelLocationID: yup.string().nullable().required(),
							isLocationActive: yup.boolean().nullable()
						})
					)
				})
				.required(),

		[]
	);

	const { showError, showSuccess } = useAlert();
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();

	const [locations, setLocations] = useState([]);

	const fillForm = async () => {
		const defaultValues = {
			saleChannel: storeName,
			inventoryCalculateMethod: null,
			inventoryUpdateMethod: null,
			inventorySync: false,
			multiLocation: false,
			pickupLocationList: [
				{ warehouseID: null, branchID: null, salesChannelLocationID: null, isLocationActive: false }
			]
		};
		if (!credentialsID) return defaultValues;
		try {
			const response = await axiosPrivate.post(SALES_CHANNELS.GET_INVENTORY_CONF_BY_SALECHANNEL, {
				salesChannelCredentialsID: credentialsID
			});
			const { data } = response;
			setLocations(data?.locations?.locations);

			return {
				...defaultValues,
				...data,
				saleChannel: storeName
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);
	const isLoading = isFormLoading || isSubmitting;
	const onSubmit = async (data) => {
		try {
			const response = await axiosPrivate.post(SALES_CHANNELS.UPDATE_INVENTORY_CONF_BY_SALECHANNEL, {
				salesChannelCredentialsID: credentialsID,
				inventoryCalculateMethod: data.inventoryCalculateMethod,
				inventoryUpdateMethod: data.inventoryUpdateMethod,
				inventorySync: data.inventorySync,
				multiLocation: data.multiLocation,
				pickupLocationList: data.pickupLocationList?.map((item) => ({
					ID: item?.ID ?? null,
					warehouseID: item.warehouseID ?? null,
					branchID: item.branchID ?? null,
					salesChannelLocationID: item.salesChannelLocationID ?? null,
					isLocationActive: item.isLocationActive ?? null
				}))
			});

			const { status } = response;
			if (status === 200) {
				showSuccess();
				modal.remove();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<Modal fullwidth maxWidth="large" className="modal-orderOtomationRule">
			<ModalTitle>
				<Typography variant="heading2">inventorySyncSettings</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="saleChannel" isLoading={isLoading} required>
						<StringInput name="saleChannel" disabled />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="inventoryCalculateMethod" isLoading={isLoading} required>
						<EnumSelect
							valueRemovable={false}
							placeholder="selectOne"
							searchable={false}
							name="inventoryCalculateMethod"
							enumName={ENUMS.INVENTORY_CALCULATE_METHOD}
							value={watch('inventoryCalculateMethod')}
							setValue={(value) => {
								setValue('inventoryCalculateMethod', value ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="inventoryUpdateMethod" isLoading={isLoading} required>
						<EnumSelect
							valueRemovable={false}
							placeholder="selectOne"
							searchable={false}
							name="inventoryUpdateMethod"
							enumName={ENUMS.INVENTORY_UPDATE_METHOD}
							value={watch('inventoryUpdateMethod')}
							setValue={(value) => {
								setValue('inventoryUpdateMethod', value ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="inventorySync" isLoading={isLoading}>
						<SwitchInput name="inventorySync" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="multiLocation" isLoading={isLoading}>
						<SwitchInput name="multiLocation" />
					</FormItem>
					{locations?.length > 0 && (
						<>
							<Alert content="pickupLocations" className="m-3 mt-5" showIcon />
							<FormItem isLoading={isLoading}>
								<PickupLocations locations={locations} />
							</FormItem>
						</>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label={'cancel'} variant="outline" onClick={() => modal.remove()} />
				<Button
					label="save"
					onClick={handleSubmit(onSubmit, AppLogger.log)}
					disabled={!isDirty}
					loading={isLoading}
					disabledTooltip={isDirty && 'formInfo.noChanges'}
					iconPos="right"
				/>
			</ModalActions>
		</Modal>
	);
});

export default InventorySyncSettingsForm;
